import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { USER_ROLES } from '../../utils/constants';
import contestService from '../../api/services/contestService';
import hostService from '../../api/services/hostService';
import { handleApiError, getContestStatusColor, formatDate, formatCurrency } from '../../utils/helpers';
import { CONTEST_STATUS } from '../../utils/constants';
import FileInput from '../../components/common/Fileinput';
import { getImageUrl } from '../../utils/imageHelper';

const MyContestsPage = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [contestToEdit, setContestToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contestToDelete, setContestToDelete] = useState(null);

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  useEffect(() => {
    fetchContests();
  }, []);

  const fetchContests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Super Admin gets all contests, Company Admin gets their own
      const response = isSuperAdmin
        ? await contestService.getAllContests({ pageSize: 100 })
        : await contestService.getUserContests({ pageSize: 100 });
      
      if (response.success) {
        setContests(response.data || response.contests || []);
      }
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (contest) => {
    // Check ownership for Super Admin
    // if (isSuperAdmin && contest.userId !== user.id) {
    //   showError('Super Admins can only edit their own contests');
    //   return;
    // }
    setContestToEdit(contest);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (contest) => {
    // Check ownership for Super Admin
    // if (isSuperAdmin && contest.userId !== user.id) {
    //   showError('Super Admins can only delete their own contests');
    //   return;
    // }
    setContestToDelete(contest);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      const response = await contestService.deleteContest(contestToDelete.id);
      if (response.success) {
        success('Contest deleted successfully!');
        fetchContests();
        setDeleteDialogOpen(false);
        setContestToDelete(null);
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  const handleTogglePause = async (contest) => {
    // Check ownership for Super Admin
    // if (isSuperAdmin && contest.userId !== user.id) {
    //   showError('Super Admins can only manage their own contests');
    //   return;
    // }

    try {
      const response = await contestService.togglePause(contest.id);
      if (response.success) {
        success(`Contest ${contest.status === 'ACTIVE' ? 'paused' : 'resumed'} successfully!`);
        fetchContests();
      }
    } catch (err) {
      showError(handleApiError(err));
    }
  };

  const handleManage = (contest) => {
    // Check ownership for Super Admin
    // if (isSuperAdmin && contest.userId !== user.id) {
    //   showError('Super Admins can only manage their own contests');
    //   return;
    // }
    navigate(`/admin/contests/${contest.id}`);
  };

  // Get image URL helper
  // const getImageUrl = (path) => {
  //   if (!path) return null;
  //   if (path.startsWith('http')) return path;
  //   const normalizedPath = path.replace(/\\/g, '/');
  //   const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
  //   return `${BASE_URL}/${normalizedPath}`;
  // };

  if (loading) {
    return (
      <DashboardLayout title={isSuperAdmin ? "All Contests" : "My Contests"}>
        <div className="flex items-center justify-center min-h-96">
          <Loader size="lg" text="Loading contests..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={isSuperAdmin ? "All Contests" : "My Contests"}
      subtitle={isSuperAdmin ? "View all contests in the system" : "Manage your contests"}
    >
      {/* Header with Create Button - Mobile Responsive */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="text-gray-600 text-sm sm:text-base">
            {isSuperAdmin 
              ? `Total contests: ${contests.length}`
              : `You have ${contests.length} contest${contests.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => setModalOpen(true)}
          className="w-full sm:w-auto"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Create New Contest
        </Button>
      </div>

      {error && <ErrorMessage message={error} onRetry={fetchContests} className="mb-6" />}

      {contests.length === 0 ? (
        <Card>
          <div className="text-center py-12 px-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">
              No contests yet
            </h3>
            <p className="mt-1 text-gray-500">
              Get started by creating your first contest
            </p>
            <div className="mt-6">
              <Button variant="primary" onClick={() => setModalOpen(true)}>
                Create Contest
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {contests.map((contest) => {
            const isOwner = contest.userId === user.id;
            
            return (
              <ContestCard
                key={contest.id}
                contest={contest}
                isOwner={isOwner}
                isSuperAdmin={isSuperAdmin}
                onEdit={() => handleEditClick(contest)}
                onDelete={() => handleDeleteClick(contest)}
                onTogglePause={() => handleTogglePause(contest)}
                onManage={() => handleManage(contest)}
                getImageUrl={getImageUrl}
              />
            );
          })}
        </div>
      )}

      {/* Create Contest Modal */}
      <ContestFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          fetchContests();
          setModalOpen(false);
        }}
      />

      {/* Edit Contest Modal */}
      <ContestFormModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setContestToEdit(null);
        }}
        onSuccess={() => {
          fetchContests();
          setEditModalOpen(false);
          setContestToEdit(null);
        }}
        editMode={true}
        contestData={contestToEdit}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Contest"
        message={`Are you sure you want to delete "${contestToDelete?.title}"? This will also delete all categories, contestants, and votes.`}
        confirmText="Delete"
        variant="danger"
      />
    </DashboardLayout>
  );
};

// Contest Card Component - Mobile Responsive
// Contest Card Component - Mobile Responsive
const ContestCard = ({ contest, isOwner, isSuperAdmin, onEdit, onDelete, onTogglePause, onManage, getImageUrl }) => {
  // Super Admin can manage all contests, others only their own
  const canManage = isSuperAdmin || isOwner;

  return (
    <Card className="relative">
      {/* Owner badge for Super Admin view */}
      {isSuperAdmin && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
          {isOwner ? (
            <span className="inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Your Contest
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Other User's Contest
            </span>
          )}
        </div>
      )}

      {/* Mobile: Stack layout, Desktop: Horizontal layout */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
        {/* Banner - Full width on mobile, fixed width on desktop */}
        {contest.banner ? (
          <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-lg overflow-hidden border-4 border-gray-100">
            <img
              src={getImageUrl(contest.banner)}
              alt={contest.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="w-full sm:w-48 h-40 sm:h-32 flex-shrink-0 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center border-4 border-gray-100">
            <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
        )}

        {/* Contest Details */}
        <div className="flex-1 min-w-0">
          {/* Title and Status */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {contest.title}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {contest.host?.name || 'Unknown Host'}
              </p>
            </div>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getContestStatusColor(contest.status)}`}>
              {contest.status}
            </span>
          </div>

          {/* Description - Truncated on mobile */}
          <p className="text-gray-700 text-sm mb-4 line-clamp-2 sm:line-clamp-3">
            {contest.description}
          </p>

          {/* Show owner info for Super Admin when viewing other's contests */}
          {isSuperAdmin && !isOwner && contest.user && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Owner</p>
              <p className="text-sm font-medium text-gray-900">
                {contest.user.firstName} {contest.user.lastName}
              </p>
              <p className="text-xs text-gray-600">{contest.user.email}</p>
            </div>
          )}

          {/* Stats Grid - Responsive */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Categories</p>
              <p className="text-lg sm:text-xl font-semibold text-gray-900">
                {contest._count?.categories || 0}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Contestants</p>
              <p className="text-lg sm:text-xl font-semibold text-gray-900">
                {contest._count?.contestants || 0}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Vote Price</p>
              <p className="text-lg sm:text-xl font-semibold text-gray-900">
                {formatCurrency(contest.votePrice)}
              </p>
            </div>
          </div>

          {/* Dates - Stack on mobile */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 mb-4">
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="truncate">Start: {formatDate(contest.startDate)}</span>
            </div>
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="truncate">End: {formatDate(contest.endDate)}</span>
            </div>
          </div>

          {/* Action Buttons - Stack on mobile, inline on desktop */}
          {canManage && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={onManage}
                className="w-full sm:w-auto"
              >
                Manage Contest
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="w-full sm:w-auto"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Button>
              
              {contest.status === 'ACTIVE' && (
                <Button
                  variant={contest.isPaused ? 'success' : 'warning'}
                  size="sm"
                  onClick={onTogglePause}
                  className="w-full sm:w-auto"
                >
                  {contest.isPaused ? 'Resume' : 'Pause'}
                </Button>
              )}

              <Button
                variant="danger"
                size="sm"
                onClick={onDelete}
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Contest Form Modal Component - WITH EDIT MODE
const ContestFormModal = ({ isOpen, onClose, onSuccess, editMode = false, contestData = null }) => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hostsLoading, setHostsLoading] = useState(false);
  const [hosts, setHosts] = useState([]);
  const [formData, setFormData] = useState({
    hostId: '',
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    votePrice: '0',
    banner: null
  });
  const [errors, setErrors] = useState({});
  const [existingBanner, setExistingBanner] = useState(null);

  const isSuperAdmin = user?.role === USER_ROLES.SUPER_ADMIN;

  // Fetch hosts and populate form data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchHosts();
      
      if (editMode && contestData) {
        // Populate form with existing contest data
        setFormData({
          hostId: contestData.hostId || contestData.host?.id || '',
          title: contestData.title || '',
          description: contestData.description || '',
          startDate: contestData.startDate ? new Date(contestData.startDate).toISOString().slice(0, 16) : '',
          endDate: contestData.endDate ? new Date(contestData.endDate).toISOString().slice(0, 16) : '',
          votePrice: contestData.votePrice?.toString() || '0',
          banner: null
        });
        setExistingBanner(contestData.banner);
      } else {
        // Reset form for create mode
        setFormData({
          hostId: '',
          title: '',
          description: '',
          startDate: '',
          endDate: '',
          votePrice: '0',
          banner: null
        });
        setExistingBanner(null);
      }
      setErrors({});
    }
  }, [isOpen, editMode, contestData]);

  const fetchHosts = async () => {
    try {
      setHostsLoading(true);
      const response = isSuperAdmin 
        ? await hostService.getAllHosts({ pageSize: 100 })
        : await hostService.getMyHosts({ pageSize: 100 });
      
      if (response.success) {
        const hostsData = response.data || [];
        setHosts(hostsData);
        
        if (hostsData.length === 0) {
          showError('No hosts available. Please create a host first.');
        }
      } else {
        showError('Failed to load hosts');
      }
    } catch (err) {
      console.error('Error fetching hosts:', err);
      showError(handleApiError(err));
    } finally {
      setHostsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (file) => {
    setFormData(prev => ({ ...prev, banner: file }));
    if (errors.banner) {
      setErrors(prev => ({ ...prev, banner: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.hostId) newErrors.hostId = 'Host is required';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    
    if (formData.startDate && formData.endDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (parseFloat(formData.votePrice) < 0) {
      newErrors.votePrice = 'Vote price must be 0 or greater';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();
      
      data.append('hostId', String(formData.hostId));
      data.append('title', formData.title.trim());
      data.append('description', formData.description.trim());
      data.append('startDate', new Date(formData.startDate).toISOString());
      data.append('endDate', new Date(formData.endDate).toISOString());
      data.append('votePrice', String(formData.votePrice));
      
      if (formData.banner) {
        data.append('banner', formData.banner);
      }

      let response;
      if (editMode && contestData) {
        response = await contestService.updateContest(contestData.id, data);
        success('Contest updated successfully!');
      } else {
        response = await contestService.createContest(data);
        success('Contest created successfully!');
      }

      if (response.success) {
        onSuccess();
      }
    } catch (err) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} contest:`, err);
      showError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Get image URL helper
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const normalizedPath = path.replace(/\\/g, '/');
    const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';
    return `${BASE_URL}/${normalizedPath}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editMode ? "Edit Contest" : "Create New Contest"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {hostsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader size="sm" text="Loading hosts..." />
          </div>
        ) : hosts.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              ⚠️ No hosts available. Please create a host organization first.
            </p>
          </div>
        ) : (
          <Select
            label="Host Organization"
            name="hostId"
            value={formData.hostId}
            onChange={handleChange}
            options={hosts.map(h => ({ 
              value: h.id, 
              label: h.name 
            }))}
            placeholder="Select a host"
            error={errors.hostId}
            required
          />
        )}

        <Input
          label="Contest Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Best Artist 2025"
          error={errors.title}
          required
        />

        <Input
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Vote for the best artist of the year"
          error={errors.description}
          rows={3}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Start Date"
            type="datetime-local"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            error={errors.startDate}
            required
          />

          <Input
            label="End Date"
            type="datetime-local"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            error={errors.endDate}
            required
          />
        </div>

        <Input
          label="Vote Price (GHS)"
          type="number"
          name="votePrice"
          value={formData.votePrice}
          onChange={handleChange}
          placeholder="0.00"
          step="0.01"
          min="0"
          error={errors.votePrice}
          required
        />

        {/* Show existing banner in edit mode */}
        {editMode && existingBanner && !formData.banner && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Current Banner
            </label>
            <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-gray-200">
              <img
                src={getImageUrl(existingBanner)}
                alt="Current banner"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-xs text-gray-500">
              Upload a new image below to replace the current banner
            </p>
          </div>
        )}

        <FileInput
          label={editMode && existingBanner ? "New Banner Image (optional)" : "Banner Image"}
          name="banner"
          onChange={handleFileChange}
          accept="image/*"
          preview
          error={errors.banner}
        />

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={hostsLoading || hosts.length === 0}
            className="flex-1 w-full sm:w-auto"
          >
            {editMode ? 'Update Contest' : 'Create Contest'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default MyContestsPage;